import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface VideoItem {
  id: string;
  filename: string;
  size: number;
  uploadDate: string;
  url: string;
  tags: string[];
  description: string;
  title?: string;
  group: string;
}

const SearchPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTag, setSearchTag] = useState('');
  const [searchGroup, setSearchGroup] = useState('');
  const [searchResults, setSearchResults] = useState<VideoItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const performSearch = async () => {
    if (!searchQuery && !searchTag) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('q', searchQuery);
      if (searchTag) params.append('tag', searchTag);
      if (searchGroup) params.append('group', searchGroup);

      const response = await fetch(`/api/videos/search?${params.toString()}`);
      
      if (response.ok) {
        const results = await response.json();
        setSearchResults(results);
      } else {
        setError('搜索失败');
      }
    } catch (err) {
      setError('网络错误');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      performSearch();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery, searchTag]);

  const handleVideoClick = (videoId: string) => {
    // 跳转到播放页面并传递视频ID
    navigate(`/?video=${videoId}`);
  };

  // 获取所有标签
  const getAllTags = () => {
    const allTags = new Set<string>();
    searchResults.forEach(video => {
      video.tags.forEach(tag => allTags.add(tag));
    });
    return Array.from(allTags);
  };

  // 获取所有分组
  const getAllGroups = () => {
    const allGroups = new Set<string>();
    searchResults.forEach(video => {
      allGroups.add(video.group);
    });
    return Array.from(allGroups);
  };

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* 搜索栏 */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-4 text-center">🔍 视频搜索</h1>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">关键词搜索</label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="输入文件名或描述关键词..."
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">标签搜索</label>
              <select
                value={searchTag}
                onChange={(e) => setSearchTag(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="">选择标签...</option>
                {getAllTags().map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1">分组搜索</label>
              <select
                value={searchGroup}
                onChange={(e) => setSearchGroup(e.target.value)}
                className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="">选择分组...</option>
                {getAllGroups().map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 搜索结果 */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="text-white text-lg">搜索中...</div>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-400">
            <div className="text-4xl mb-4">⚠️</div>
            <p>{error}</p>
          </div>
        ) : searchResults.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <div className="text-4xl mb-4">📁</div>
            <p>没有找到匹配的视频</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {searchResults.map(video => (
              <div 
                key={video.id} 
                className="bg-gray-900 rounded-lg overflow-hidden cursor-pointer hover:bg-gray-800 transition-colors"
                onClick={() => handleVideoClick(video.id)}
              >
                <div className="aspect-video bg-black flex items-center justify-center">
                  <video 
                    src={video.url} 
                    className="w-full h-full object-cover"
                    muted
                    preload="metadata"
                  />
                </div>
                <div className="p-3">
                  <h3 className="font-medium text-sm truncate">{video.filename}</h3>
                  {video.description && (
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2">{video.description}</p>
                  )}
                  {video.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {video.tags.map((tag, index) => (
                        <span 
                          key={index}
                          className="bg-blue-600/30 text-blue-300 px-2 py-1 rounded text-xs"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 返回按钮 */}
        <div className="mt-8 text-center">
          <button 
            onClick={() => navigate('/')}
            className="inline-block bg-green-600 hover:bg-green-700 px-6 py-3 rounded-lg font-medium transition-colors"
          >
            🎬 返回视频播放器
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchPage;